import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Select, MenuItem, InputLabel, FormControl, Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {  formatAmountForDisplayWithPreferences, parseMoney, sumMoney, truncateToTwoDecimals } from '../../../../../utils';
import { usePreferences } from 'hooks/usePreferences';

// Define discount/promotion types
const discountTypes = [{value: 'percentage', display: 'Percentage'}, {value: 'fixed', display: 'Fixed'}];

// Helper component for the Add/Edit Discount/Promotion Dialog
function AddEditDiscountDialog({ open, onClose, discountData, onSave }) {
    const [type, setType] = useState('');
    const [value, setValue] = useState(''); // Store as number or string initially
    const [errors, setErrors] = useState({});

    // Populate form when discountData changes (for editing)
    useEffect(() => {
        if (discountData) {
            //setDescription(discountData.description || '');
            setType(discountData.type || '');
            setValue(discountData.value || '');
        } else {
            // Reset form for adding new
            //setDescription('');
            setType('');
            setValue('');
        }
        setErrors({}); // Clear errors on open/data change
    }, [open, discountData]); // Depend on open and discountData

     const handleValueChange = (event) => {
        const inputValue = event.target.value;
        // Allow only numbers and a single decimal point
        if (/^\d*\.?\d*$/.test(inputValue)) {
            setValue(inputValue);
        }
    };


    const validate = () => {
        const newErrors = {};
        //if (!description) newErrors.description = 'Description is required';
        if (!type) newErrors.type = 'Type is required';
        if (value === '' || parseFloat(value) < 0) newErrors.value = 'Value must be a non-negative number';
         if (type.toLocaleLowerCase() === 'percentage' && (parseFloat(value) > 100 || parseFloat(value) < 0)) {
             newErrors.value = 'Percentage must be between 0 and 100';
         }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) {
            return;
        }

        const savedDiscount = {
            ...discountData, // Include id if editing
            //description,
            type,
            value: parseFloat(value), // Store value as a number
        };
        onSave(savedDiscount);
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{discountData ? 'Edit Discount/Promotion' : 'Add New Discount/Promotion'}</DialogTitle>
            <DialogContent dividers>
                {/* <TextField
                    label="Description / Code"
                    fullWidth
                    margin="normal"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    error={!!errors.description}
                    helperText={errors.description}
                /> */}

                <FormControl fullWidth margin="normal" error={!!errors.type}>
                    <InputLabel id="discount-type-label">Type</InputLabel>
                    <Select
                        labelId="discount-type-label"
                        id="discount-type-select"
                        value={type}
                        label="Type"
                        onChange={(e) => setType(e.target.value)}
                    >
                        <MenuItem value=""><em>Select Type</em></MenuItem>
                        {discountTypes.map((typeOption) => (
                            <MenuItem key={typeOption.value} value={typeOption.value}>{typeOption.display}</MenuItem>
                        ))}
                    </Select>
                    {errors.type && <Typography color="error" variant="caption">{errors.type}</Typography>}
                </FormControl>

                <TextField
                    label="Value"
                    fullWidth
                    margin="normal"
                    value={value}
                    onChange={handleValueChange}
                    error={!!errors.value}
                    helperText={errors.value}
                    InputProps={{
                        startAdornment: type === 'Fixed' ? <Typography sx={{ mr: 1 }}>$</Typography> : null,
                        endAdornment: type === 'Percentage' ? <Typography sx={{ ml: 1 }}>%</Typography> : null,
                        type: 'text', // Use text to control input format manually
                        inputMode: 'decimal'
                    }}
                />

            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained">Save</Button>
            </DialogActions>
        </Dialog>
    );
}

// Main Discount/Promotion Collector Component
export default function DiscountPromotion({
    discounts = [], // Array of discount objects { id, description, type, value }
    orderSubtotal = 0, // The subtotal *before* discounts are applied
    onAddDiscount,
    onEditDiscount,
    onDeleteDiscount,
}) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingDiscount, setEditingDiscount] = useState(null); // null for add, object for edit
    const { preferences } = usePreferences();   
    
    const handleOpenAddDialog = () => {
        setEditingDiscount(null); // Ensure we are adding
        setOpenDialog(true);
    };

    const handleOpenEditDialog = (discount) => {
        setEditingDiscount(discount); // Set discount data for editing
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleSaveDiscount = (discount) => {
        if (discount.id || discount.tempId) {
            // Editing existing discount
            if (onEditDiscount) {
                onEditDiscount(discount);
            }
        } else {
            // Adding new discount
            const newDiscount = { ...discount, tempId: Date.now() }; // Assign a simple unique ID
            if (onAddDiscount) {
                onAddDiscount(newDiscount);
            }
        }
        handleCloseDialog(); // Close dialog after saving
    };

    const handleDeleteDiscount = (discountId) => {
        // You might want a confirmation dialog here
        if (onDeleteDiscount) {
            onDeleteDiscount(discountId);
        }
    };

    // Calculate the actual dollar amount for a single discount
    const calculateDiscountAmount = useCallback((discount) => {
        // if (!discount || typeof discount.value !== 'number' || orderSubtotal <= 0) {
        //     return 0;
        // }

        if (discount.type.toLowerCase() === 'percentage') {
            // Ensure percentage is between 0 and 100
            const percentage = Math.max(0, Math.min(100, parseMoney(discount.value)));
            return truncateToTwoDecimals((percentage / 100) * orderSubtotal);
        } else if (discount.type.toLowerCase() === 'fixed') {
            // Ensure fixed amount doesn't exceed subtotal (optional, depending on rules)
             return Math.min(parseMoney(discount.value), orderSubtotal); // Don't give more discount than the subtotal
            // Or just return the value if fixed amounts can exceed subtotal:
        }
        return 0;
    }, [orderSubtotal]); // Depend on orderSubtotal

    // Calculate the total discount applied
    const totalDiscountAmount = useMemo(() => sumMoney(discounts.map(calculateDiscountAmount)), [calculateDiscountAmount, discounts]);
    
    //discounts.reduce((sum, discount) => { return sum + calculateDiscountAmount(discount);}, 0);


    return (
        <Box sx={{ mt: 1, border: '1px solid #ccc', p: 1, borderRadius: 1, maxHeight: '300px', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>Discounts & Promotions</Typography>

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleOpenAddDialog}
                sx={{ mb: 2 }}
            >
                Add Discount/Promotion
            </Button>

             {discounts.length === 0 ? (
                 <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    No discounts or promotions applied yet.
                 </Typography>
             ) : (
                 <TableContainer >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {/* <TableCell>Description</TableCell> */}
                                <TableCell>Type</TableCell>
                                <TableCell align="right">Value</TableCell>
                                <TableCell align="right">Applied Amount</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {discounts.map((discount) => (
                                <TableRow 
                                    key={discount.id || discount.tempId}
                                    sx={{
                                        opacity: discount._markedForDeletion ? 0.4 : 1,
                                        textDecoration: discount._markedForDeletion ? 'line-through' : 'none'
                                    }}
                                >
                                    {/* <TableCell>{discount.description}</TableCell> */}
                                    <TableCell>
                                        {discount._markedForDeletion && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                    px: 0.5,
                                                    py: 0.25,
                                                    backgroundColor: '#d32f2f',
                                                    borderRadius: 0.5,
                                                    display: 'inline-block',
                                                    opacity: 1
                                                }}
                                            >
                                                MARKED FOR DELETION
                                            </Typography>
                                        )}
                                        {discount.type}
                                    </TableCell>
                                    <TableCell align="right">
                                        {discount.type.toLowerCase() === 'fixed' ? '$' : ''}
                                        {(parseMoney(discount.value) || 0).toFixed(2)}
                                        {discount.type.toLowerCase() === 'percentage' ? '%' : ''}
                                    </TableCell>
                                     <TableCell align="right">
                                        -{(formatAmountForDisplayWithPreferences(calculateDiscountAmount(discount), preferences))}
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenEditDialog(discount)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => handleDeleteDiscount(discount.id || discount.tempId)}>
                                            <DeleteIcon fontSize="small" color="error" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
             )}


            <Box sx={{ mt: 2, textAlign: 'right' }}>
                 <Typography variant="h6">Total Discount Applied: -{(formatAmountForDisplayWithPreferences(totalDiscountAmount, preferences ))}</Typography>
            </Box>

            {/* Add/Edit Discount/Promotion Dialog */}
            <AddEditDiscountDialog
                open={openDialog}
                onClose={handleCloseDialog}
                discountData={editingDiscount}
                onSave={handleSaveDiscount}
            />
        </Box>
    );
}